import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  TablePagination,
  Avatar,
  Paper,
  Switch,
  Menu,
} from "@mui/material";
import {
  Users,
  Store,
  Package,
  Search,
  RefreshCw,
  ExternalLink,
  Eye,
  Trash2,
  LogIn,
  MoreVertical,
  Shield,
  Edit,
  TrendingUp,
  AlertTriangle,
  Phone,
  UserPlus,
} from "lucide-react";
import Axios from "../../../Common/api/axiosConfig";
import { uploadLink } from "../../../utils/constants";
import TenantDetailsModal from "./TenantDetailsModal";
import EditTenantModal from "./EditTenantModal";
import CreateTenantModal from "./CreateTenantModal";

interface TenantRecord {
  _id: string;
  email: string;
  role: "tenant" | "superadmin";
  status: "active" | "suspended";
  isVerified: boolean;
  createdAt: string;
  store?: {
    _id?: string;
    name?: string;
    url?: string;
    number?: number;
    address?: string;
    visibility?: boolean;
    status?: string;
    header?: any;
    theme?: any;
  };
  storeCount?: number;
  stores?: any[];
  productCount: number;
  warrantyCount: number;
  orders: number;
  revenue: number;
  storeViews: number;
}

interface PlatformStats {
  totalTenants: number;
  totalUsers: number;
  activeTenants: number;
  suspendedTenants: number;
  totalStores: number;
  activeStores: number;
  totalProducts: number;
  totalWarrantyRegistrations: number;
  totalRevenue: number;
  totalOrders: number;
  totalStoreViews: number;
}

export const AdminTenants: React.FC = () => {
  document.title = "Tenant Management | Rudrix Superadmin";

  // Data States
  const [tenants, setTenants] = useState<TenantRecord[]>([]);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [statsLoading, setStatsLoading] = useState<boolean>(true);

  // Filters & Search
  const [search, setSearch] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [roleFilter, setRoleFilter] = useState<string>("all");

  // Pagination
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Modals & Action States
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [tenantToEdit, setTenantToEdit] = useState<TenantRecord | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [tenantToDelete, setTenantToDelete] = useState<TenantRecord | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  // Action Menu State
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTenant, setMenuTenant] = useState<TenantRecord | null>(null);

  // Toast State
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const showToast = (
    message: string,
    severity: "success" | "error" | "info" | "warning" = "success"
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  // Fetch Platform Stats
  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const res = await Axios.get("/admin/stats");
      setStats(res.data.data);
    } catch (err: any) {
      console.error("Failed to load platform stats:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch Tenants List
  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (search.trim()) params.search = search.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      if (roleFilter !== "all") params.role = roleFilter;

      const res = await Axios.get("/admin/tenants", { params });
      setTenants(res.data.data || []);
      setTotalCount(res.data.pagination?.total || 0);
    } catch (err: any) {
      console.error("Failed to fetch tenants:", err);
      showToast(
        err?.response?.data?.message || "Failed to load tenants list",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter, roleFilter]);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  // Handle Tenant Impersonation ("Login As Tenant")
  const handleImpersonate = async (tenantId: string, email: string) => {
    try {
      const res = await Axios.post(`/admin/tenants/${tenantId}/impersonate`);
      const { token, user } = res.data;

      // Backup current superadmin session
      const currentToken = localStorage.getItem("authToken");
      const currentUser = localStorage.getItem("userData");

      if (currentToken && currentUser) {
        localStorage.setItem("adminBackupToken", currentToken);
        localStorage.setItem("adminBackupUser", currentUser);
      }

      // Set tenant token and user
      localStorage.setItem("authToken", token);
      localStorage.setItem("userData", JSON.stringify(user));

      showToast(`Switched session to ${email}. Redirecting...`, "info");
      window.location.href = "/dashboard";
    } catch (err: any) {
      console.error("Impersonation error:", err);
      showToast(
        err?.response?.data?.message || "Failed to impersonate tenant",
        "error"
      );
    }
  };

  // Handle Quick Status Toggle (Active <-> Suspended)
  const handleToggleStatus = async (tenant: TenantRecord) => {
    const newStatus = tenant.status === "active" ? "suspended" : "active";
    try {
      await Axios.patch(`/admin/tenants/${tenant._id}/status`, {
        status: newStatus,
      });
      showToast(`Tenant ${tenant.email} marked as ${newStatus}`, "success");
      fetchTenants();
      fetchStats();
    } catch (err: any) {
      console.error("Error updating status:", err);
      showToast(
        err?.response?.data?.message || "Failed to update status",
        "error"
      );
    }
  };

  // Handle Role Toggle (Tenant <-> Superadmin)
  const handleToggleRole = async (tenant: TenantRecord) => {
    const newRole = tenant.role === "superadmin" ? "tenant" : "superadmin";
    try {
      await Axios.patch(`/admin/tenants/${tenant._id}/role`, {
        role: newRole,
      });
      showToast(
        `User ${tenant.email} role changed to ${newRole}`,
        "success"
      );
      fetchTenants();
      fetchStats();
    } catch (err: any) {
      console.error("Error updating role:", err);
      showToast(
        err?.response?.data?.message || "Failed to update role",
        "error"
      );
    }
  };

  // Handle Tenant Deletion
  const handleDeleteTenant = async () => {
    if (!tenantToDelete) return;
    try {
      setDeleting(true);
      await Axios.delete(`/admin/tenants/${tenantToDelete._id}`);
      showToast(`Tenant ${tenantToDelete.email} deleted successfully`, "success");
      setShowDeleteModal(false);
      setTenantToDelete(null);
      fetchTenants();
      fetchStats();
    } catch (err: any) {
      console.error("Error deleting tenant:", err);
      showToast(
        err?.response?.data?.message || "Failed to delete tenant",
        "error"
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    tenant: TenantRecord
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuTenant(tenant);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuTenant(null);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary" }}>
            Superadmin Tenant Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Full oversight and controls across all tenant stores, inventory, and accounts
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<UserPlus size={16} />}
            onClick={() => setShowCreateModal(true)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Add Tenant
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshCw size={16} />}
            onClick={() => {
              fetchTenants();
              fetchStats();
            }}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Platform Metric Overview Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                TOTAL TENANTS
              </Typography>
              <Avatar
                sx={{
                  bgcolor: "primary.light",
                  color: "primary.main",
                  width: 38,
                  height: 38,
                }}
              >
                <Users size={20} />
              </Avatar>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
              {statsLoading ? "-" : stats?.totalTenants ?? 0}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Chip
                label={`${stats?.activeTenants ?? 0} Active`}
                size="small"
                color="success"
                sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
              />
              {Number(stats?.suspendedTenants ?? 0) > 0 && (
                <Chip
                  label={`${stats?.suspendedTenants} Suspended`}
                  size="small"
                  color="error"
                  sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                />
              )}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                ACTIVE STORES
              </Typography>
              <Avatar
                sx={{
                  bgcolor: "success.light",
                  color: "success.main",
                  width: 38,
                  height: 38,
                }}
              >
                <Store size={20} />
              </Avatar>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
              {statsLoading ? "-" : stats?.totalStores ?? 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stats?.activeStores ?? 0} stores online & visible
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                TOTAL PRODUCTS
              </Typography>
              <Avatar
                sx={{
                  bgcolor: "info.light",
                  color: "info.main",
                  width: 38,
                  height: 38,
                }}
              >
                <Package size={20} />
              </Avatar>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
              {statsLoading ? "-" : stats?.totalProducts ?? 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Listed across all storefronts
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                PLATFORM REVENUE
              </Typography>
              <Avatar
                sx={{
                  bgcolor: "warning.light",
                  color: "warning.main",
                  width: 38,
                  height: 38,
                }}
              >
                <TrendingUp size={20} />
              </Avatar>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, color: "success.main" }}>
              ₹{statsLoading ? "-" : (stats?.totalRevenue ?? 0).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stats?.totalOrders ?? 0} total orders recorded
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Search & Filter Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          placeholder="Search by store name, email, URL, phone..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: { xs: "100%", sm: 320 }, flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="filter-status-label">Status</InputLabel>
          <Select
            labelId="filter-status-label"
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="active">Active Only</MenuItem>
            <MenuItem value="suspended">Suspended Only</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="filter-role-label">Role</InputLabel>
          <Select
            labelId="filter-role-label"
            label="Role"
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">All Roles</MenuItem>
            <MenuItem value="tenant">Tenants</MenuItem>
            <MenuItem value="superadmin">Superadmins</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Tenants Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Table sx={{ minWidth: 850 }}>
          <TableHead sx={{ backgroundColor: "action.hover" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Tenant / Store</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Owner Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>
                Inventory
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>
                Orders & Rev
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>
                Status
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                  <CircularProgress size={36} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    Loading tenants directory...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : tenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                  <Users size={40} className="text-secondary" />
                  <Typography variant="body1" sx={{ mt: 1.5, fontWeight: 600 }}>
                    No tenants found
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Try adjusting your search criteria or status filter.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tenants.map((tenant) => {
                const store = tenant.store;
                const storeUrl = store?.url
                  ? store.url.includes(".")
                    ? `https://${store.url}`
                    : `/${store.url}`
                  : null;

                const logoSrc = store?.header?.logo
                  ? `${uploadLink}${store.header.logo}`
                  : null;

                return (
                  <TableRow
                    key={tenant._id}
                    hover
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    {/* Store Info */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          src={logoSrc || undefined}
                          variant="rounded"
                          sx={{
                            width: 42,
                            height: 42,
                            bgcolor: "primary.light",
                            color: "primary.main",
                            fontWeight: 700,
                          }}
                        >
                          {store?.name ? store.name.charAt(0).toUpperCase() : <Store size={20} />}
                        </Avatar>
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {store?.name || "No Store Configured"}
                            </Typography>
                            {tenant.stores && tenant.stores.length > 1 && (
                              <Chip
                                label={`+${tenant.stores.length - 1} more`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                              />
                            )}
                          </Box>
                          {storeUrl ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Typography
                                variant="caption"
                                color="primary"
                                component="a"
                                href={storeUrl}
                                target="_blank"
                                rel="noreferrer"
                                sx={{ textDecoration: "none", fontWeight: 600 }}
                              >
                                {store?.url}
                              </Typography>
                              <ExternalLink size={12} className="text-primary" />
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No public slug
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Owner Email & Role */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tenant.email}
                      </Typography>
                      <Chip
                        label={tenant.role === "superadmin" ? "Superadmin" : "Tenant"}
                        size="small"
                        color={tenant.role === "superadmin" ? "secondary" : "default"}
                        sx={{ height: 20, fontSize: 10, fontWeight: 700, mt: 0.5 }}
                      />
                    </TableCell>

                    {/* Phone / WhatsApp */}
                    <TableCell>
                      {store?.number ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Phone size={14} className="text-secondary" />
                          <Typography variant="body2">+{store.number}</Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          None
                        </Typography>
                      )}
                    </TableCell>

                    {/* Inventory */}
                    <TableCell align="center">
                      <Chip
                        label={`${tenant.productCount} Products`}
                        size="small"
                        variant="outlined"
                        color={tenant.productCount > 0 ? "primary" : "default"}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>

                    {/* Orders & Revenue */}
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        ₹{(tenant.revenue || 0).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tenant.orders || 0} orders
                      </Typography>
                    </TableCell>

                    {/* Status Toggle */}
                    <TableCell align="center">
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                        <Chip
                          label={tenant.status === "suspended" ? "Suspended" : "Active"}
                          size="small"
                          color={tenant.status === "suspended" ? "error" : "success"}
                          sx={{ fontWeight: 700, fontSize: 11 }}
                        />
                        <Tooltip title={tenant.status === "active" ? "Suspend tenant" : "Activate tenant"}>
                          <Switch
                            size="small"
                            checked={tenant.status === "active"}
                            onChange={() => handleToggleStatus(tenant)}
                            color="success"
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right">
                      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                        <Tooltip title="View Tenant Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedTenantId(tenant._id);
                              setShowDetailsModal(true);
                            }}
                          >
                            <Eye size={18} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Login As Tenant (Impersonate)">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleImpersonate(tenant._id, tenant.email)}
                          >
                            <LogIn size={18} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="More Options">
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, tenant)}
                          >
                            <MoreVertical size={18} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* Tenant Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem
          onClick={() => {
            if (menuTenant) {
              setTenantToEdit(menuTenant);
              setShowEditModal(true);
            }
            handleCloseMenu();
          }}
        >
          <Edit size={16} style={{ marginRight: 8 }} />
          Edit Store Details
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (menuTenant) {
              handleToggleRole(menuTenant);
            }
            handleCloseMenu();
          }}
        >
          <Shield size={16} style={{ marginRight: 8 }} />
          {menuTenant?.role === "superadmin"
            ? "Demote to Tenant"
            : "Promote to Superadmin"}
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (menuTenant) {
              setTenantToDelete(menuTenant);
              setShowDeleteModal(true);
            }
            handleCloseMenu();
          }}
          sx={{ color: "error.main" }}
        >
          <Trash2 size={16} style={{ marginRight: 8 }} />
          Delete Tenant
        </MenuItem>
      </Menu>

      {/* Create Tenant Modal */}
      <CreateTenantModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => {
          fetchTenants();
          fetchStats();
          showToast("Tenant created successfully!", "success");
        }}
      />

      {/* Tenant Details Modal */}
      <TenantDetailsModal
        open={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedTenantId(null);
        }}
        tenantId={selectedTenantId}
        onImpersonate={handleImpersonate}
      />

      {/* Edit Tenant Modal */}
      <EditTenantModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setTenantToEdit(null);
        }}
        tenant={tenantToEdit}
        onSaved={() => {
          fetchTenants();
          fetchStats();
          showToast("Tenant updated successfully", "success");
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AlertTriangle color="#dc2626" size={22} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Confirm Tenant Deletion
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Are you sure you want to permanently delete tenant{" "}
            <strong>{tenantToDelete?.email}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ fontSize: 13 }}>
            This action will cascade delete the tenant account, linked store, products,
            analytics, and warranty records. This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setShowDeleteModal(false)}
            variant="outlined"
            disabled={deleting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTenant}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <Trash2 size={16} />}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Delete Tenant
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Alert */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%", boxShadow: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminTenants;
